import api from './api';
import practiceService from './practiceService';
import { resolveDisplayScheduleTargets, hasAnyScheduleTargets } from '../utils/scheduleTargetUtils';
import {
  getUsePatientScheduleTargetsPreference,
  setPatientHasCustomTargets
} from '../utils/patientScheduleTargetPreference.js';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Create axios instance with default config
// const api = axios.create({
//   baseURL: API_URL,
//   withCredentials: true,
//   headers: {
//     'Content-Type': 'application/json',
//   },
// });

const patientService = {
  // Create patient
  createPatient: async (practiceId, patientData) => {
    const isFormData = patientData instanceof FormData;
    const config = isFormData
      ? {
          headers: { 'Content-Type': 'multipart/form-data' },
          // Disable axios body/time caps for multipart requests; the server
          // enforces its own limits via multer. This allows e.g. profile
          // picture + signatures to flow through without spurious 413s.
          timeout: 0,
          maxBodyLength: Infinity,
          maxContentLength: Infinity
        }
      : {};
    const response = await api.post(`/practices/${practiceId}/patients`, patientData, config);
    return response;
  },

  // Get all patients for a practice
  getPatients: async (practiceId, params = {}) => {
    const response = await api.get(`/practices/${practiceId}/patients`, { params });
    return response;
  },

  // Get patient by ID
  getPatient: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}`);
    return response;
  },

  // Fast patient details for initial UI render
  getPatientDetailsFast: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/details-fast`);
    return response;
  },

  // Lightweight patient header for Measurement screens (name, dob, phone, device)
  getMeasurementHeaderFast: async (practiceId, patientId) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/details-fast`,
      { params: { view: 'measurement' } }
    );
    return response;
  },

  // Get next account number for a practice
  getNextAccountNumber: async (practiceId) => {
    const response = await api.get(`/practices/${practiceId}/patients/next-account-number`);
    return response;
  },

  // Update patient
  updatePatient: async (practiceId, patientId, patientData) => {
    const isFormData = patientData instanceof FormData;
    const config = isFormData
      ? {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 0,
          maxBodyLength: Infinity,
          maxContentLength: Infinity
        }
      : {};
    const response = await api.put(`/practices/${practiceId}/patients/${patientId}`, patientData, config);
    return response;
  },

  // Delete patient
  deletePatient: async (practiceId, patientId) => {
    const response = await api.delete(`/practices/${practiceId}/patients/${patientId}`);
    return response;
  },

  // Get patient details with all related data
  getPatientDetails: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/details`);
    return response;
  },

  // Get patient schedule and targets
  getScheduleAndTargets: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/schedule-targets`);
    return response;
  },

  // Update patient schedule and targets
  updateScheduleAndTargets: async (practiceId, patientId, data) => {
    const response = await api.put(`/practices/${practiceId}/patients/${patientId}/schedule-targets`, data);
    return response;
  },


  // Follow-up notes
  createFollowUp: async (practiceId, patientId, followUpData) => {
    const response = await api.post(`/practices/${practiceId}/patients/${patientId}/follow-ups`, followUpData);
    if (practiceId) {
      practiceService.invalidatePracticePatientsCache(practiceId);
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('patient:followup-changed', { detail: { practiceId, patientId } }));
    }
    return response;
  },

  getFollowUps: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/follow-ups`);
    return response;
  },

  updateFollowUp: async (practiceId, patientId, followUpId, followUpData) => {
    const response = await api.put(`/practices/${practiceId}/patients/${patientId}/follow-ups/${followUpId}`, followUpData);
    if (practiceId) {
      practiceService.invalidatePracticePatientsCache(practiceId);
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('patient:followup-changed', { detail: { practiceId, patientId } }));
    }
    return response;
  },

  deleteFollowUp: async (practiceId, patientId, followUpId) => {
    const response = await api.delete(`/practices/${practiceId}/patients/${patientId}/follow-ups/${followUpId}`);
    if (practiceId) {
      practiceService.invalidatePracticePatientsCache(practiceId);
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('patient:followup-changed', { detail: { practiceId, patientId } }));
    }
    return response;
  },

  // ============================================================================
  // METER MANAGEMENT FUNCTIONS
  // ============================================================================

  // Add meter to patient
  addMeterToPatient: async (practiceId, patientId, meterData) => {
    const response = await api.post(`/practices/${practiceId}/patients/${patientId}/meters`, meterData);
    return response;
  },

  // Delete meter from patient
  deleteMeterFromPatient: async (practiceId, patientId, meterId) => {
    const response = await api.delete(`/practices/${practiceId}/patients/${patientId}/meters/${meterId}`);
    return response;
  },

  // Get patient meters
  getPatientMeters: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/meters`);
    return response;
  },

  // Get patient meter mappings (for edit mode)
  getPatientMeterMappings: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/meters`);
    return response;
  },

  // ============================================================================
  // PATIENT LOOKUP FUNCTIONS
  // ============================================================================

  // Lookup patients with filters
  lookupPatient: async (practiceId, filters = {}) => {
    const params = {};

    // Only add non-empty filter parameters
    if (filters.lastName && filters.lastName.trim()) {
      params['lastName'] = filters.lastName.trim();
    }
    if (filters.firstName && filters.firstName.trim()) {
      params['firstName'] = filters.firstName.trim();
    }
    // Don't send patient-practice since practiceId is already in the URL path
    if (filters.provider && filters.provider.toString().trim()) {
      params['patient-provider'] = filters.provider.toString().trim();
    }
    if (filters.caregiver && filters.caregiver.toString().trim()) {
      params['patient-caregiver'] = filters.caregiver.toString().trim();
    }
    if (filters.status && filters.status.toString().trim()) {
      params['patient-status'] = filters.status.toString().trim();
    }
    if (filters.vitals && filters.vitals.toString().trim()) {
      params['patient-vitals'] = filters.vitals.toString().trim();
    }
    if (filters.serialNumber && filters.serialNumber.trim()) {
      params['patient_serial_number'] = filters.serialNumber.trim();
    }
    if (filters.page) {
      params['page'] = filters.page;
    }
    if (filters.limit) {
      params['limit'] = filters.limit;
    }

    const response = await api.get(`/practices/${practiceId}/lookup`, { params });
    return response;
  },

  // Lookup patients by RPM date range
  lookupPatientRPM: async (practiceId, filters = {}) => {
    const params = {};

    // Only add non-empty date parameters
    // Don't send practiceId since it's already in the URL path
    const startDate = filters.start || filters.billingStartRpm || '';
    const endDate = filters.end || filters.billingEndRpm || '';

    if (startDate && startDate.trim()) {
      params['billingStartRpm'] = startDate.trim();
    }
    if (endDate && endDate.trim()) {
      params['billingEndRpm'] = endDate.trim();
    }
    if (filters.page) {
      params.page = filters.page;
    }
    if (filters.limit) {
      params.limit = filters.limit;
    }

    const response = await api.get(`/practices/${practiceId}/lookup-rpm`, { params });
    return response;
  },

  // Get current logged-in patient (by user_id)
  getCurrentPatient: async () => {
    const response = await api.get('/patients/me');
    return response;
  },

  // ============================================================================
  // DASHBOARD OPTIMIZATION APIS (fast, lazy-load)
  // ============================================================================

  /**
   * getDashboardSummary — replaces 4 sequential API calls on PatientDashboard.
   * Single endpoint: parallel DB queries + 60s server-side cache.
   * Target response time: < 150ms.
   */
  getDashboardSummary: async (practiceId, patientId) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/dashboard-summary`
    );
    return response;
  },

  /**
   * getPatientNotes — lazy-load, called only when Notes section is expanded.
   */
  getPatientNotes: async (practiceId, patientId) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/notes`
    );
    return response;
  },

  /**
   * getMeasurementSummary — lazy-load, called when "Summary of Measurement" tab opens.
   * Returns 7-day, 1-month, 3-month averages for BP, glucose, weight.
   */
  getMeasurementSummary: async (practiceId, patientId) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurement-summary`
    );
    return response;
  },

  /**
   * getPatientUploads — lazy-load paginated upload history.
   * @param {number} page  - Page number (default 1)
   * @param {number} limit - Items per page (default 20)
   */
  getPatientUploads: async (practiceId, patientId, page = 1, limit = 20) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/uploads`,
      { params: { page, limit } }
    );
    return response;
  },

  /**
   * getPatientDocuments — fetches files from patient_document table.
   */
  getPatientDocuments: async (practiceId, patientId) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/documents`
    );
    return response;
  },

  patchPatientDocument: async (practiceId, patientId, documentId, payload) => {
    const response = await api.patch(
      `/practices/${practiceId}/patients/${patientId}/documents/${documentId}`,
      payload
    );
    return response;
  },

  deletePatientDocument: async (practiceId, patientId, documentId) => {
    const response = await api.delete(
      `/practices/${practiceId}/patients/${patientId}/documents/${documentId}`
    );
    return response;
  },

  /**
   * Upload a SINGLE document file for an existing patient (max 10 MB).
   *
   * @param {number|string} practiceId
   * @param {number|string} patientId      patients.id (NOT user_id)
   * @param {File} file                    raw browser File
   * @param {string} [displayName]         optional override for the saved name
   */
  uploadPatientDocument: async (practiceId, patientId, file, displayName) => {
    const formData = new FormData();
    formData.append('documents', file);
    if (displayName) formData.append('document_name', displayName);

    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/documents`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        // Avoid duplicate toasts: AddPatient shows row-level errors / one summary.
        suppressGlobal413Toast: true
      }
    );
    return response;
  },

  /**
   * GET /practices/:practiceId/check-meter-serial?serial=&exclude_user_id=
   */
  checkMeterSerialAvailability: async (practiceId, params) => {
    const response = await api.get(`/practices/${practiceId}/check-meter-serial`, { params });
    return response;
  },

  /**
   * Raw practice + patient schedule/target payloads (no session merge).
   * Used to categorize historical readings by target mode at measurement time.
   */
  getScheduleTargetSourcesForPatient: async (practiceId, patientId) => {
    const [practiceResponse, patientResponse] = await Promise.all([
      practiceService.getScheduleAndTargets(practiceId).catch(() => null),
      patientId
        ? api.get(`/practices/${practiceId}/patients/${patientId}/schedule-targets`).catch(() => null)
        : Promise.resolve(null)
    ]);
    const practiceData = practiceResponse?.data?.success ? (practiceResponse.data.data || {}) : {};
    const patientPayload = patientResponse?.data?.success ? (patientResponse.data.data || {}) : {};
    const patientScheduleOnly = { ...patientPayload };
    delete patientScheduleOnly.usePatientScheduleTargets;
    if (practiceId && patientId) {
      setPatientHasCustomTargets(practiceId, patientId, hasAnyScheduleTargets(patientScheduleOnly));
    }
    return { practiceData, patientData: patientScheduleOnly };
  },

  /**
   * GET practice + patient schedule rows merged using this session's practice vs patient preference
   * (see patientScheduleTargetPreference.js).
   */
  getResolvedScheduleTargetsForPatient: async (practiceId, patientId) => {
    const { practiceData, patientData } = await patientService.getScheduleTargetSourcesForPatient(
      practiceId,
      patientId
    );
    const usePatientTargets = getUsePatientScheduleTargetsPreference(practiceId, patientId, patientData);
    const effective = resolveDisplayScheduleTargets({
      practiceData,
      patientData,
      usePatientTargets
    });
    if (hasAnyScheduleTargets(effective)) return effective;
    return practiceData;
  }
};

export default patientService;
